import type { ConversationSummary } from '@/api/types'

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
const fullFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })
const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
]

/** "3 months ago", "yesterday", "just now". */
export function formatRelative(value: string | Date | null | undefined, now = new Date()): string {
  const date = toDate(value)
  if (!date) return 'unknown'
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 45) return 'just now'
  for (const [unit, size] of UNITS) {
    if (abs >= size || unit === 'minute') {
      return rtf.format(Math.round(seconds / size), unit)
    }
  }
  return 'just now'
}

/** "8 Oct 2026, 14:05" in the user's locale. */
export function formatFull(value: string | Date | null | undefined): string {
  const date = toDate(value)
  return date ? fullFormat.format(date) : 'Unknown date'
}

export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value)
  return date ? dateFormat.format(date) : '—'
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)} s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

export type ConversationGroup = { label: string; items: ConversationSummary[] }

const GROUP_ORDER = ['Pinned', 'Today', 'Yesterday', 'Previous 7 days', 'Older'] as const

/** Pinned / Today / Yesterday / Previous 7 days / Older, newest first within each group. */
export function groupConversations(conversations: ConversationSummary[], now = new Date()): ConversationGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const day = 24 * 3600 * 1000
  const buckets = new Map<string, ConversationSummary[]>(GROUP_ORDER.map((g) => [g, []]))

  const sorted = [...conversations].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
  for (const conversation of sorted) {
    let label: (typeof GROUP_ORDER)[number]
    const updated = new Date(conversation.updated_at).getTime()
    if (conversation.pinned) label = 'Pinned'
    else if (updated >= startOfToday) label = 'Today'
    else if (updated >= startOfToday - day) label = 'Yesterday'
    else if (updated >= startOfToday - 7 * day) label = 'Previous 7 days'
    else label = 'Older'
    buckets.get(label)?.push(conversation)
  }
  return GROUP_ORDER.map((label) => ({ label, items: buckets.get(label) ?? [] })).filter(
    (group) => group.items.length > 0,
  )
}
